import * as React from 'react';
import ProjectsInfoModal from './ProjectsInfoModal';
import DiMarkdown from './DiMarkdown';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

interface Insight {
  id: string;
  title: string;
  content: string;
  task: string;
  ai_assistant: string;
  created_at: string;
}

function NoInsightsMessage() {
  return (
    <div className="no-insights-message">
      <i className="material-icons">lightbulb</i>
      <h3>No Insights Yet</h3>
      <p>Your AI-generated insights will appear here as your projects progress.</p>
    </div>
  );
}

function MyInsights({ insights: initialInsights }: { insights: Insight[] }) {
  const [insights, setInsights] = React.useState<Insight[]>(initialInsights);
  const [expandedInsight, setExpandedInsight] = React.useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [insightToDelete, setInsightToDelete] = React.useState<string | null>(null);
  const [copyNotification, setCopyNotification] = React.useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(insights.length / itemsPerPage);

  const getCurrentPageInsights = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return insights.slice(startIndex, startIndex + itemsPerPage);
  };

  const toggleInsight = (id: string) => {
    setExpandedInsight(prevId => prevId === id ? null : id);
  };

  const openDeleteModal = (id: string) => {
    setInsightToDelete(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setInsightToDelete(null);
  };

  const deleteInsight = async () => {
    if (insightToDelete) {
      try {
        const response = await fetch(`/insights/${insightToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
        });

        if (response.ok) {
          setInsights(prevInsights => prevInsights.filter(insight => insight.id !== insightToDelete));
          closeDeleteModal();
        } else {
          console.error('Failed to delete insight');
        }
      } catch (error) {
        console.error('Error deleting insight:', error);
      }
    }
  };

  const copyToClipboard = (content: string, insightId: string) => {
    navigator.clipboard.writeText(content);
    setCopyNotification(insightId);
    // Clear the notification after 2 seconds
    setTimeout(() => {
      setCopyNotification(null);
    }, 2000);
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title">My Insights</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item"><a href="/my_projects">My Projects</a></li>
            <li className="breadcrumb-item active" aria-current="page">My Insights</li>
          </ol>
        </nav>
      </div>

      <p className="insights-intro">
        Your curated AI-generated insights. <ProjectsInfoModal message="Learn how it works" />
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-lg-9 col-md-12">
                  {insights.length > 0 ? (
                    <>
                      {getCurrentPageInsights().map((insight) => (
                        <div key={insight.id} className="insight-card" onClick={() => toggleInsight(insight.id)}>
                          <div className="insight-header">
                            <h6 className="insight-title">{insight.title}</h6>
                            <div className="insight-meta">
                              <span>AI: {insight.ai_assistant}</span>
                              <span>Created: {insight.created_at}</span>
                            </div>
                            <div className="insight-expand-indicator">
                              <Tooltip title={expandedInsight === insight.id ? "Collapse" : "Expand"}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInsight(insight.id);
                                  }}
                                  size="small"
                                  aria-label={expandedInsight === insight.id ? "Collapse" : "Expand"}
                                  className="expand-indicator-btn"
                                >
                                  {expandedInsight === insight.id ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          {expandedInsight === insight.id && (
                            <div className="insight-content">
                              <p>
                                <DiMarkdown>{insight.content}</DiMarkdown>
                              </p>
                              {insight.task && (
                                <p><h3>Parent Task</h3>
                                <span>The above insights were generated for the following tasks:</span>
                                <DiMarkdown>{insight.task}</DiMarkdown>
                                </p>
                              )}
                            </div>
                          )}
                          <div className="insight-actions">
                            <Tooltip title="View Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/insights/${insight.id}`, '_blank');
                                }}
                                size="small"
                                aria-label="View Insight"
                                className="insight-action-btn"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(insight.content, insight.id);
                                }}
                                size="small"
                                aria-label="Copy Insight"
                                className="insight-action-btn"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const element = document.createElement("a");
                                  const file = new Blob([insight.content], { type: 'text/plain' });
                                  element.href = URL.createObjectURL(file);
                                  element.download = `${insight.title?.slice(0, 20) || 'insight'}_insight.txt`;
                                  document.body.appendChild(element);
                                  element.click();
                                  document.body.removeChild(element);
                                }}
                                size="small"
                                aria-label="Download Insight"
                                className="insight-action-btn"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(insight.id);
                                }}
                                size="small"
                                aria-label="Delete Insight"
                                className="insight-action-btn delete-btn"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </div>
                      ))}

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                          >
                            Previous
                          </button>
                          <span className="pagination-info">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoInsightsMessage />
                  )}
                </div>
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteModal && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <h2>Confirm Deletion</h2>
                    <p>Are you sure you want to delete this insight?</p>
                    <div className="modal-actions">
                      <button onClick={closeDeleteModal} className="cancel-btn">Cancel</button>
                      <button onClick={deleteInsight} className="confirm-delete-btn">Delete</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Copy Notification */}
              {copyNotification && (
                <div className="copy-notification">
                  <span>Copied to clipboard!</span>
                </div>
              )}

              {/* Styles */}
              <style>{`
                .insight-card {
                  border: 1px solid #e0e0e0;
                  border-radius: 0.5rem;
                  padding: 1rem;
                  margin-bottom: 1rem;
                  cursor: pointer;
                  transition: background-color 0.3s ease, box-shadow 0.3s ease;
                }
                .insight-card:hover {
                  background-color: #f8f9fa;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                .insight-header {
                  display: flex;
                  flex-direction: column;
                  margin-bottom: 0.5rem;
                  position: relative;
                }
                .insight-title {
                  margin-bottom: 0.5rem;
                  color: #2c3e50;
                  font-weight: 600;
                  padding-right: 3rem;
                }
                .insight-meta {
                  font-size: 0.875rem;
                  color: #6c757d;
                }
                .insight-meta span {
                  margin-left: 1rem;
                }
                .insight-expand-indicator {
                  position: absolute;
                  top: 0;
                  right: 0;
                  z-index: 2;
                }
                .expand-indicator-btn {
                  color: #007bff !important;
                  transition: all 0.2s ease !important;
                  border-radius: 50% !important;
                  padding: 0.5rem !important;
                  min-width: 40px !important;
                  height: 40px !important;
                  background-color: rgba(0, 123, 255, 0.1) !important;
                }
                .expand-indicator-btn:hover {
                  background-color: rgba(0, 123, 255, 0.2) !important;
                  color: #0056b3 !important;
                  transform: translateY(-1px);
                }
                .expand-indicator-btn:active {
                  transform: translateY(0);
                }
                .insight-content {
                  margin-top: 0.5rem;
                  padding: 0.75rem;
                  background-color: #f8f9fa;
                  border-radius: 0.375rem;
                  border-left: 3px solid #007bff;
                }
                .insight-actions {
                  display: flex;
                  justify-content: flex-end;
                  margin-top: 0.5rem;
                  flex-wrap: wrap;
                  gap: 0.25rem;
                  align-items: center;
                }
                .insight-action-btn {
                  color: #6c757d !important;
                  transition: all 0.2s ease !important;
                  border-radius: 50% !important;
                  padding: 0.375rem !important;
                  min-width: 32px !important;
                  height: 32px !important;
                }
                .insight-action-btn:hover {
                  background-color: rgba(0, 0, 0, 0.04) !important;
                  color: #495057 !important;
                  transform: translateY(-1px);
                }
                .insight-action-btn.delete-btn {
                  color: #dc3545 !important;
                }
                .insight-action-btn.delete-btn:hover {
                  background-color: rgba(220, 53, 69, 0.1) !important;
                  color: #c82333 !important;
                }
                .insight-action-btn:active {
                  transform: translateY(0);
                }
                @media (max-width: 768px) {
                  .insight-header {
                    flex-direction: column;
                    align-items: flex-start;
                  }
                  .insight-meta span {
                    display: block;
                    margin-left: 0;
                  }
                  .insight-title {
                    padding-right: 2.5rem;
                  }
                  .expand-indicator-btn {
                    min-width: 36px !important;
                    height: 36px !important;
                    padding: 0.375rem !important;
                  }
                  .insight-actions {
                    gap: 0.125rem;
                    justify-content: center;
                  }
                  .insight-action-btn {
                    min-width: 28px !important;
                    height: 28px !important;
                    padding: 0.25rem !important;
                  }
                }
                @media (max-width: 480px) {
                  .insight-title {
                    padding-right: 2rem;
                  }
                  .expand-indicator-btn {
                    min-width: 32px !important;
                    height: 32px !important;
                    padding: 0.25rem !important;
                  }
                  .insight-actions {
                    gap: 0.125rem;
                    justify-content: center;
                    flex-wrap: wrap;
                  }
                  .insight-action-btn {
                    min-width: 24px !important;
                    height: 24px !important;
                    padding: 0.125rem !important;
                  }
                }
                .row {
                  display: flex;
                  flex-wrap: wrap;
                }
                .col-lg-9 {
                  width: 75%;
                }
                @media (max-width: 992px) {
                  .col-lg-9 {
                    width: 100%;
                  }
                }

                .modal-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-color: rgba(0, 0, 0, 0.5);
                  display: flex;
                  justify-content: center;
                  align-items: center;
                }

                .modal-content {
                  background-color: white;
                  padding: 2rem;
                  border-radius: 0.5rem;
                  width: 90%;
                  max-width: 500px;
                }

                .modal-actions {
                  display: flex;
                  justify-content: flex-end;
                  margin-top: 1rem;
                }

                .modal-actions button {
                  margin-left: 0.5rem;
                  padding: 0.5rem 1rem;
                  border: none;
                  border-radius: 0.25rem;
                  cursor: pointer;
                }

                .cancel-btn {
                  background-color: #6c757d;
                  color: white;
                }

                .confirm-delete-btn {
                  background-color: #dc3545;
                  color: white;
                }

                .pagination {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin-top: 2rem;
                  gap: 1rem;
                }

                .pagination-btn {
                  padding: 0.5rem 1rem;
                  border: 1px solid #dee2e6;
                  background-color: white;
                  border-radius: 0.25rem;
                  cursor: pointer;
                }

                .pagination-btn:disabled {
                  background-color: #e9ecef;
                  cursor: not-allowed;
                }

                .pagination-info {
                  color: #6c757d;
                }

                .insights-intro {
                  margin-bottom: 2rem;
                  color: #6c757d;
                }

                .insights-intro :global(button) {
                  margin-left: 0.5rem;
                  color: #007bff;
                  background: none;
                  border: none;
                  padding: 0;
                  text-decoration: underline;
                  cursor: pointer;
                }

                .no-insights-message {
                  text-align: center;
                  padding: 40px;
                  background-color: #f8f9fa;
                  border-radius: 8px;
                  color: #6c757d;
                  margin: 20px 0;
                }

                .no-insights-message .material-icons {
                  font-size: 48px;
                  margin-bottom: 16px;
                  color: #ffd700;
                }

                .no-insights-message h3 {
                  margin-bottom: 8px;
                  font-size: 1.5rem;
                  color: #495057;
                }

                .no-insights-message p {
                  font-size: 1rem;
                  max-width: 400px;
                  margin: 0 auto;
                }

                .copy-notification {
                  position: fixed;
                  bottom: 20px;
                  right: 20px;
                  background-color: #4caf50;
                  color: white;
                  padding: 10px 20px;
                  border-radius: 4px;
                  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                  z-index: 1000;
                  animation: fadeInOut 2s ease-in-out;
                }

                @keyframes fadeInOut {
                  0% { opacity: 0; transform: translateY(20px); }
                  15% { opacity: 1; transform: translateY(0); }
                  85% { opacity: 1; transform: translateY(0); }
                  100% { opacity: 0; transform: translateY(-20px); }
                }

                .dark-mode .insight-card,
                .dark-mode .modal-content,
                .dark-mode .pagination-btn,
                .dark-mode .no-insights-message {
                  background-color: #222328;
                  border-color: #322f2f;
                  color: #ffffff;
                }

                .dark-mode .insight-card:hover {
                  background-color: #1a191d;
                }

                .dark-mode .insight-title,
                .dark-mode .no-insights-message h3 {
                  color: #ffffff;
                }

                .dark-mode .insight-meta,
                .dark-mode .pagination-info,
                .dark-mode .insights-intro,
                .dark-mode .no-insights-message p {
                  color: #9c9fa6;
                }

                .dark-mode .expand-indicator-btn {
                  color: #b66dff !important;
                  background-color: rgba(182, 109, 255, 0.16) !important;
                }

                .dark-mode .expand-indicator-btn:hover {
                  background-color: rgba(182, 109, 255, 0.26) !important;
                  color: #da8cff !important;
                }

                .dark-mode .insight-content {
                  background-color: #151519;
                  border-left-color: #b66dff;
                }

                .dark-mode .insight-action-btn {
                  color: #9c9fa6 !important;
                }

                .dark-mode .insight-action-btn:hover {
                  background-color: rgba(255, 255, 255, 0.06) !important;
                  color: #ffffff !important;
                }

                .dark-mode .pagination-btn:disabled {
                  background-color: #1a191d;
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyInsights;
